import axios from 'axios';
import { store } from '../../store/store';
import { logout, updateAccessToken, updateUserRole, rehydrateSession } from '../../store/authSlice';
import { extractRoleFromToken, getPersistedAccessToken, readPersistedSession, writePersistedSession } from '../../shared/authSession';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.host}/api/v1`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const persistedToken = getPersistedAccessToken();
    const state = store.getState();
    const stateToken = state.auth?.accessToken;

    // Prefer the persisted token since localStorage is shared across tabs and represents the freshest session.
    // If persisted token exists and differs from in-memory state, sync Redux.
    const token = persistedToken || stateToken;
    if (persistedToken && persistedToken !== stateToken) {
      store.dispatch(updateAccessToken(persistedToken));
    }

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const refreshPromiseByToken = new Map();

export const clearRefreshPromises = () => {
  refreshPromiseByToken.clear();
};

export const redirectToLogin = () => {
  if (typeof window !== 'undefined' && window.location?.pathname !== '/login') {
    window.location.href = '/login';
  }
};

export const getRefreshKey = (originalRequest) => {
  const requestAuthHeader = originalRequest?.headers?.Authorization || originalRequest?.headers?.authorization;
  const requestToken = requestAuthHeader ? String(requestAuthHeader).replace(/^Bearer\s+/i, '') : null;
  const session = readPersistedSession();
  const activeRefreshToken = session?.refreshToken || store.getState()?.auth?.refreshToken;
  return requestToken || getPersistedAccessToken() || activeRefreshToken || 'anonymous';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Don't handle 401 for auth endpoints - these are expected to return 401 for invalid credentials
    // Also exclude tenant info endpoint as it's public info needed before login
    const authEndpoints = ['/auth/login', '/auth/login/sso', '/auth/forgot-password', '/auth/reset-password', '/auth/tenant', '/auth/refresh'];
    const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      const requestAuthHeader = originalRequest.headers?.Authorization || originalRequest.headers?.authorization;
      const requestToken = requestAuthHeader ? String(requestAuthHeader).replace(/^Bearer\s+/i, '') : null;
      const currentPersistedToken = getPersistedAccessToken();

      // Case 1: Another tab or concurrent request in this tab ALREADY refreshed the access token!
      if (currentPersistedToken && requestToken && currentPersistedToken !== requestToken) {
        store.dispatch(updateAccessToken(currentPersistedToken));
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${currentPersistedToken}`;
        return api(originalRequest);
      }

      // Case 2: We need to perform the refresh.
      // Guard refresh state per auth context so unrelated auth flows are not serialized.
      const refreshKey = getRefreshKey(originalRequest);

      if (!refreshPromiseByToken.has(refreshKey)) {
        const doRefresh = (async () => {
          const state = store.getState();
          const session = readPersistedSession();
          const refreshToken = session?.refreshToken || state.auth?.refreshToken;

          if (!refreshToken) {
            const noTokenErr = new Error('No refresh token available');
            noTokenErr.isAuthFailure = true;
            throw noTokenErr;
          }

          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          });

          const newAccessToken = response.data?.data?.accessToken;
          if (!newAccessToken) {
            throw new Error('Refresh response missing access token');
          }

          const newRole = response.data?.data?.role || extractRoleFromToken(newAccessToken);

          store.dispatch(updateAccessToken(newAccessToken));
          if (newRole) {
            store.dispatch(updateUserRole(newRole));
          }

          const currentSession = readPersistedSession();
          const existingUser = currentSession?.user || store.getState().auth?.user || null;
          const updatedUser = existingUser ? {
            ...existingUser,
            ...(newRole ? { role: newRole } : {}),
            accessToken: newAccessToken
          } : null;

          writePersistedSession({
            user: updatedUser,
            accessToken: newAccessToken,
            refreshToken: currentSession?.refreshToken || refreshToken || null
          });

          return newAccessToken;
        })().finally(() => {
          refreshPromiseByToken.delete(refreshKey);
        });

        refreshPromiseByToken.set(refreshKey, doRefresh);
      }

      try {
        const newAccessToken = await refreshPromiseByToken.get(refreshKey);
        refreshPromiseByToken.delete(refreshKey);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        refreshPromiseByToken.delete(refreshKey);
        // Before wiping the session and redirecting, check if another tab has
        // established a new authenticated session in localStorage (e.g. user logged in on another tab).
        const latestSession = readPersistedSession();
        const latestAccessToken = latestSession?.accessToken;
        const failedRefreshToken = store.getState()?.auth?.refreshToken;

        if (latestSession && latestAccessToken && latestSession.refreshToken !== failedRefreshToken) {
          // Another tab logged in with a fresh session! Rehydrate rather than logging out.
          store.dispatch(rehydrateSession());
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${latestAccessToken}`;
          return api(originalRequest);
        }

        // Check if this error is an actual authentication rejection vs temporary network error
        const isNetworkError =
          refreshError.code === 'ERR_NETWORK' ||
          refreshError.message === 'Network Error' ||
          (refreshError.response && refreshError.response.status >= 500);

        const authFailure = new Error('Refresh token invalid or expired');
        authFailure.isAuthFailure = true;
        authFailure.cause = refreshError;
        if (refreshError?.response) {
          authFailure.response = refreshError.response;
        }

        if (!isNetworkError) {
          store.dispatch(logout());
          redirectToLogin();
          return Promise.reject(authFailure);
        }

        return Promise.reject(refreshError);
      }
    }

    const apiMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.response?.data?.data?.error ||
      error.response?.data?.data?.message;

    if (apiMessage) {
      error.message = apiMessage;
    }

    return Promise.reject(error);
  }
);

export default api;
